import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { Background } from "../../components/Auth/Background";
import { Button } from "../../components/Button";
import ConfirmModal from "../../components/ConfirmModal";
import Input from "../../components/Form/Input/input";
import InputVerificationCode from "../../components/Form/InputVerificationCode";
import { Form, FormGroup } from "../../components/Form/styles";
import { Box } from "../../components/Layout";
import { SocialProof } from "../../components/SocialProof";
import SymbolKeepoIcon from "../../icons/SymbolKeepo";
import * as authenticationService from "../../services/auth";
import { getMissingPasswordRequirementMessages, isPasswordRequirementMessage } from "../../utils/passwordRequirements";

type RecoveryStep = 1 | 2 | 3;

type RecoveryErrorKey =
  | "cannotRecoverPassword"
  | "emailNotFound"
  | "activeRecoveryToken"
  | "noRecoveryCodeRequested"
  | "recoveryCodeExpired"
  | "invalidCode"
  | "invalidToken"
  | "invalidPassword"
  | "generic";

const normalizeErrorMessage = (value: string) => {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]/g, "");
};

const mapRecoveryErrorKey = (message: string): RecoveryErrorKey => {
  const normalizedMessage = normalizeErrorMessage(message);

  if (normalizedMessage.includes("cannotrecoverpassword")) {
    return "cannotRecoverPassword";
  }

  if (normalizedMessage.includes("emailnotfound") || normalizedMessage.includes("usernotfound")) {
    return "emailNotFound";
  }

  if (
    normalizedMessage.includes("passwordrecoverytokenisalreadyactive") ||
    normalizedMessage.includes("activerecoverytoken")
  ) {
    return "activeRecoveryToken";
  }

  if (
    normalizedMessage.includes("norecoverycodewasrequested") ||
    normalizedMessage.includes("norecoverycoderequested")
  ) {
    return "noRecoveryCodeRequested";
  }

  if (normalizedMessage.includes("recoverycodeexpired")) {
    return "recoveryCodeExpired";
  }

  if (normalizedMessage.includes("invalidverificationcode") || normalizedMessage.includes("invalidcode")) {
    return "invalidCode";
  }

  if (normalizedMessage.includes("invalidtoken")) {
    return "invalidToken";
  }

  if (
    normalizedMessage.includes("invalidpassword") ||
    normalizedMessage.includes("passworddoesnotmeet") ||
    normalizedMessage.includes("pelomenos") ||
    normalizedMessage.includes("atleast")
  ) {
    return "invalidPassword";
  }

  return "generic";
};

const getRecoveryErrorMessages = (message: string, password: string, t: (key: string) => string) => {
  if (isPasswordRequirementMessage(message)) {
    return getMissingPasswordRequirementMessages(password, t);
  }

  const key = mapRecoveryErrorKey(message);
  return [t(`pages.recoveryPassword.errors.${key}`)];
};

const RecoverPasswordPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [step, setStep] = useState<RecoveryStep>(1);
  const [email, setEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [recoveryToken, setRecoveryToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setLoading] = useState(false);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [isDone, setDone] = useState(false);

  const submitEmail = async () => {
    if (!email || isLoading) {
      return;
    }

    setErrorMessages([]);
    setLoading(true);
    try {
      const response = await authenticationService.requestRecoveryPassword(email);
      if (!response.recoveryCodeSent) {
        throw new Error("RECOVERY_CODE_NOT_SENT");
      }

      setEmail(response.email || email);
      setStep(2);
    } catch (e) {
      setErrorMessages(getRecoveryErrorMessages((e as Error).message, password, t));
    } finally {
      setLoading(false);
    }
  };

  const submitVerificationCode = async () => {
    if (!email || verificationCode.length !== 5 || isLoading) {
      return;
    }

    setErrorMessages([]);
    setLoading(true);
    try {
      const response = await authenticationService.verifyRecoveryPasswordCode(email, verificationCode);
      if (!response.recoveryAllowed || !response.recoveryToken) {
        throw new Error("RECOVERY_NOT_ALLOWED");
      }

      setEmail(response.email || email);
      setRecoveryToken(response.recoveryToken);
      setStep(3);
    } catch (e) {
      setErrorMessages(getRecoveryErrorMessages((e as Error).message, password, t));
    } finally {
      setLoading(false);
    }
  };

  const submitNewPassword = async () => {
    if (!email || !recoveryToken || !password || !confirmPassword || password !== confirmPassword || isLoading) {
      return;
    }

    const passwordErrors = getMissingPasswordRequirementMessages(password, t);
    if (passwordErrors.length > 0) {
      setErrorMessages(passwordErrors);
      return;
    }

    setErrorMessages([]);
    setLoading(true);
    try {
      const response = await authenticationService.resetPassword({
        email,
        recoveryToken,
        password,
      });

      if (!response.passwordChanged) {
        throw new Error("PASSWORD_NOT_CHANGED");
      }

      setDone(true);
    } catch (e) {
      setErrorMessages(getRecoveryErrorMessages((e as Error).message, password, t));
    } finally {
      setLoading(false);
    }
  };

  const messages =
    step === 1
      ? {
          title: t("pages.recoveryPassword.step1Title"),
          description: t("pages.recoveryPassword.step1Description"),
        }
      : step === 2
        ? {
            title: t("pages.recoveryPassword.step2Title"),
            description: t("pages.recoveryPassword.step2Description"),
          }
        : {
            title: t("pages.recoveryPassword.step3Title"),
            description: t("pages.recoveryPassword.step3Description"),
          };

  return (
    <div className="auth-screen">
      <div className="content">
        <header>
          <SymbolKeepoIcon />
        </header>
        <div className="space-y-8">
          <div>
            <h3 className="typography-h3 mb-1">{messages.title}</h3>
            <p className="typography-p">{messages.description}</p>
          </div>
          <Box className="!mx-0 !mt-0" borderBottom>
            <Form
              className="flex flex-col"
              onSubmit={e => {
                e.preventDefault();
                // Mirrors each step's button disabled state (Button is a styled div and doesn't block clicks)
                if (step === 1 && email && !isLoading) {
                  submitEmail();
                } else if (step === 2 && verificationCode.length === 5 && !isLoading) {
                  submitVerificationCode();
                } else if (step === 3 && password && confirmPassword && password === confirmPassword && !isLoading) {
                  submitNewPassword();
                }
              }}>
              {step === 1 && (
                <>
                  <FormGroup>
                    <Input
                      onChange={e => setEmail(e.target.value)}
                      label={t("pages.recoveryPassword.form.emailLabel")}
                      value={email}
                      id="email"
                      type="email"
                      placeholder={t("pages.recoveryPassword.form.emailPlaceholder")}
                    />
                  </FormGroup>
                  <Button onClick={submitEmail} type="INFO" size="LG" block disabled={!email || isLoading}>
                    {isLoading
                      ? t("pages.recoveryPassword.buttons.loading")
                      : t("pages.recoveryPassword.buttons.sendCode")}
                  </Button>
                </>
              )}
              {step === 2 && (
                <>
                  <FormGroup className="flex !flex-row gap-4">
                    <InputVerificationCode value={verificationCode} onChange={setVerificationCode} />
                  </FormGroup>
                  <Button
                    onClick={submitVerificationCode}
                    type="INFO"
                    size="LG"
                    block
                    disabled={verificationCode.length !== 5 || isLoading}>
                    {isLoading
                      ? t("pages.recoveryPassword.buttons.loading")
                      : t("pages.recoveryPassword.buttons.verifyCode")}
                  </Button>
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="typography-p mt-4 text-center text-xs font-bold underline">
                    {t("pages.recoveryPassword.buttons.back")}
                  </button>
                </>
              )}
              {step === 3 && (
                <>
                  <FormGroup>
                    <Input
                      onChange={e => setPassword(e.target.value)}
                      label={t("pages.recoveryPassword.form.passwordLabel")}
                      value={password}
                      id="password"
                      type="password"
                      placeholder={t("pages.recoveryPassword.form.passwordPlaceholder")}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Input
                      onChange={e => setConfirmPassword(e.target.value)}
                      label={t("pages.recoveryPassword.form.confirmPasswordLabel")}
                      value={confirmPassword}
                      id="confirmPassword"
                      type="password"
                      placeholder={t("pages.recoveryPassword.form.confirmPasswordPlaceholder")}
                    />
                  </FormGroup>
                  <Button
                    onClick={submitNewPassword}
                    type="INFO"
                    size="LG"
                    block
                    disabled={!password || !confirmPassword || password !== confirmPassword || isLoading}>
                    {isLoading
                      ? t("pages.recoveryPassword.buttons.loading")
                      : t("pages.recoveryPassword.buttons.resetPassword")}
                  </Button>
                  <button
                    type="button"
                    onClick={() => setStep(2)}
                    className="typography-p mt-4 text-center text-xs font-bold underline">
                    {t("pages.recoveryPassword.buttons.back")}
                  </button>
                </>
              )}
              {/* The visible Buttons are styled divs; this lets Enter submit the form */}
              <button type="submit" className="sr-only" tabIndex={-1} aria-hidden="true" />
            </Form>
          </Box>
          {errorMessages.length > 0 && (
            <div className="space-y-1">
              {errorMessages.map(errorMessage => (
                <p key={errorMessage} className="typography-p text-red-500">
                  {errorMessage}
                </p>
              ))}
            </div>
          )}
          <div>
            <p className="typography-p font-bold text-white">
              {t("pages.login.register.text")}{" "}
              <Link to="/register" className="text-white underline underline-offset-2 hover:text-shark-300">
                {t("pages.login.register.linkText")}
              </Link>
            </p>
          </div>
        </div>
        <footer>
          <SocialProof />
        </footer>
      </div>
      <Background />
      {isDone && (
        <ConfirmModal
          type="INFO"
          title={t("pages.recoveryPassword.successModal.message")}
          confirmButtonTitle={t("pages.recoveryPassword.successModal.buttonTitle")}
          onConfirm={() => navigate("/login")}
        />
      )}
    </div>
  );
};

export default RecoverPasswordPage;
